/**
 * Utility functions for locale handling and normalization
 *
 * This module provides unified locale normalization logic used across OPAY and Adyen services.
 * It ensures consistent locale handling by:
 * - Normalizing language codes (fr, en, nl) to full locales (fr-FR, en-US, nl-NL)
 * - Supporting regional variants (fr-CA, en-GB)
 * - Validating locale format (ISO 639-1 + ISO 3166-1)
 * - Rejecting malformed locales
 * - Defaulting to fr-FR (French as ultimate fallback)
 */
import { AVAILABLE_LANGUAGE_TAGS, LANGUAGE_MAP, SUPPORTED_TRANSLATIONS, } from "@/domain/types";
// Default locale when no match is found (French as ultimate fallback)
export const DEFAULT_LANGUAGE_TAG = "fr-FR";
/**
 * Validates if a locale string follows ISO 639-1 + ISO 3166-1 format
 * Valid formats: "fr-FR", "en-US", "nl-NL", "fr", "en", "nl"
 * Invalid formats: "f", "fra", "_FR", "fr_", "fr-FR-BE", etc.
 *
 * @param locale - The locale string to validate
 * @returns True if the locale format is valid, false otherwise
 */
const isValidLocaleFormat = (locale) => {
    if (!locale || typeof locale !== "string") {
        return false;
    }
    const trimmed = locale.trim();
    // Empty string is invalid
    if (trimmed.length === 0) {
        return false;
    }
    // Check for language code only (2 letters, e.g., "fr", "en")
    const languageCodeOnly = /^[a-z]{2}$/i.test(trimmed);
    if (languageCodeOnly) {
        return true;
    }
    // Check for full locale format (language-country, e.g., "fr-FR", "en-US")
    // Must be exactly: 2 letters, hyphen, 2 letters
    const fullLocalePattern = /^[a-z]{2}-[a-z]{2}$/i;
    if (fullLocalePattern.test(trimmed)) {
        return true;
    }
    // Reject malformed locales (e.g., "f", "fra", "_FR", "fr_", "fr-FR-BE")
    return false;
};
/**
 * Normalizes any locale string to a canonical full locale format (ISO 639-1 + ISO 3166-1)
 *
 * This is the single source of truth for locale normalization across OPAY and Adyen.
 * It handles:
 * - Language codes (fr → fr-FR, en → en-GB, nl → nl-NL)
 * - Language tags with region (fr-FR → fr-FR, fr-CA → fr-CA, en-US → en-US, en-GB → en-GB)
 * - Locale variants (fr-BE → fr-FR, en-AU → en-GB)
 * - Invalid/malformed locales (f, fra, _FR → fr-FR default)
 * - Empty/null/undefined inputs → fr-FR default
 *
 * @param locale - The locale string to normalize (can be language code or full locale)
 * @returns The normalized full locale (fr-FR, fr-CA, en-US, en-GB, or nl-NL), defaults to fr-FR
 *
 * @example
 * normalizeLocale("fr") // Returns "fr-FR"
 * normalizeLocale("fr-FR") // Returns "fr-FR"
 * normalizeLocale("fr-CA") // Returns "fr-CA"
 * normalizeLocale("fr-BE") // Returns "fr-FR"
 * normalizeLocale("en") // Returns "en-GB"
 * normalizeLocale("en-US") // Returns "en-US"
 * normalizeLocale("en-GB") // Returns "en-GB"
 * normalizeLocale("nl") // Returns "nl-NL"
 * normalizeLocale("f") // Returns "fr-FR" (invalid format, defaults)
 * normalizeLocale("fra") // Returns "fr-FR" (invalid format, defaults)
 * normalizeLocale("") // Returns "fr-FR" (empty, defaults)
 * normalizeLocale(undefined) // Returns "fr-FR" (undefined, defaults)
 */
export const normalizeLocale = (locale) => {
    // Handle null, undefined, or non-string input
    if (!locale || typeof locale !== "string") {
        return DEFAULT_LANGUAGE_TAG;
    }
    const trimmed = locale.trim();
    // Handle empty string
    if (trimmed.length === 0) {
        return DEFAULT_LANGUAGE_TAG;
    }
    // Validate locale format - reject malformed locales
    if (!isValidLocaleFormat(trimmed)) {
        return DEFAULT_LANGUAGE_TAG;
    }
    // Normalize to lowercase for comparison
    const lowerTrimmed = trimmed.toLowerCase();
    // Check for exact matches with supported regional variants (case-insensitive)
    if (lowerTrimmed === "en-gb")
        return "en-GB";
    if (lowerTrimmed === "en-us")
        return "en-US";
    if (lowerTrimmed === "fr-ca")
        return "fr-CA";
    if (lowerTrimmed === "fr-fr")
        return "fr-FR";
    if (lowerTrimmed === "nl-nl")
        return "nl-NL";
    // Extract language code (first 2 characters)
    const languageCode = lowerTrimmed.substring(0, 2);
    // Map language code to full locale
    if (LANGUAGE_MAP[languageCode]) {
        return LANGUAGE_MAP[languageCode];
    }
    // If language code is not supported, default to fr-FR
    return DEFAULT_LANGUAGE_TAG;
};
/**
 * Normalizes a locale string specifically for Adyen SDK integration.
 * Adyen often falls back to English when a regional variant (like fr-CA) is not fully supported
 * for certain payment method components.
 *
 * This function handles these cases by falling back to the base language's primary regional
 * variant if strictly necessary for Adyen.
 *
 * @param locale - The locale string to normalize
 * @returns The normalized locale safe for Adyen SDK
 */
export const normalizeLocaleForAdyen = (locale) => {
    const normalized = normalizeLocale(locale);
    // If normalized locale is fr-CA, fall back to fr-FR for Adyen to avoid English labels
    if (normalized === "fr-CA") {
        return "fr-FR";
    }
    // If normalized locale is en-GB, fall back to en-US
    if (normalized === "en-GB") {
        return "en-US";
    }
    return normalized;
};
/**
 * Extracts the language code (first two letters) from a locale string
 *
 * @param locale - The locale string (e.g., "fr-FR", "en-US")
 * @returns The language code in lowercase (e.g., "fr", "en")
 */
export const getLanguageCode = (locale) => {
    return locale.substring(0, 2).toLowerCase();
};
/**
 * Checks if a locale is supported by the application
 *
 * @param locale - The locale string to check
 * @returns True if the locale is exactly supported, false otherwise
 */
export const isLanguageTagSupported = (locale) => {
    return AVAILABLE_LANGUAGE_TAGS.includes(locale);
};
/**
 * Checks if a locale is a normalized language tag that is supported by the application
 *
 * @param locale - The locale string to check
 * @returns True if the locale is exactly supported, false otherwise
 */
export const isNormalizedLanguageTag = (locale) => {
    return SUPPORTED_TRANSLATIONS.includes(locale);
};
/**
 * Checks if a locale's language is supported (even if the exact language tag region is not)
 *
 * @param locale - The locale string to check
 * @returns True if the language is supported, false otherwise
 *
 * @example
 * isLanguageSupported("fr-BE") // Returns true (French is supported)
 * isLanguageSupported("es-ES") // Returns false (Spanish is not supported)
 */
export const isLanguageSupported = (locale) => {
    const languageCode = getLanguageCode(locale);
    return isLanguageTagSupported(languageCode);
};
